import { useEffect } from 'react';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Grid, Chip, CircularProgress, Button, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getAnalyticsDashboardRequest, getPendingTATRequest } from '../../../redux/modules/laboratory/labAnalytics/slice';
import { getAlertsRequest, acknowledgeAlertRequest, bumpOrderPriorityRequest } from '../../../redux/modules/laboratory/labAlert/slice';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DoneIcon from '@mui/icons-material/Done';

// Escalation ladder used by the "Escalate" action on a breached order.
const NEXT_PRIORITY = { routine: 'urgent', urgent: 'stat', stat: 'stat' };
function formatOverdue(minutes) {
  if (minutes === null || minutes === undefined) return '';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `Overdue by ${h > 0 ? `${h}h ${m}m` : `${m}m`}`;
}

const StatsCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
  transition: 'all 200ms ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
}));

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 16,
});

const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: color ? `${color}15` : theme.palette.primary.light,
  color: color || theme.palette.primary.main,
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '2.25rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

const MetricRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 0',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const SummaryCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

function formatRemaining(minutes) {
  if (minutes === null || minutes === undefined) return '-';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const str = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return minutes < 0 ? `Overdue by ${str}` : `${str} left`;
}

export default function LaboratoryDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.split('/laboratory')[0];
  const { dashboardRes, loading, pendingTATRes } = useSelector(state => state.labAnalytics);
  const { alertsRes, actionLoading } = useSelector(state => state.labAlert);
  const currentBranch = useSelector(state => state.branch.currentBranch);
  const features = useSelector(state => state.hospitalConfig?.features || {});
  const tatTracking = !!features['lis.tatTracking'];
  const tatAlerts = !!features['lis.tatAlerts'];

  useEffect(() => {
    dispatch(setMainHeading('Laboratory Dashboard'));
  }, [dispatch]);

  useEffect(() => {
    if (currentBranch?.id) {
      dispatch(getAnalyticsDashboardRequest());
    }
  }, [currentBranch?.id, dispatch]);

  // TAT countdown: refresh every minute so remaining times and overdue flags
  // stay live without a manual reload.
  useEffect(() => {
    if (!currentBranch?.id || !tatTracking) return;
    dispatch(getPendingTATRequest());
    const timer = setInterval(() => dispatch(getPendingTATRequest()), 60000);
    return () => clearInterval(timer);
  }, [currentBranch?.id, tatTracking, dispatch]);

  // TAT breach alerts: poll the same way as the countdown so newly-breached
  // orders surface within a minute.
  useEffect(() => {
    if (!currentBranch?.id || !tatAlerts) return;
    dispatch(getAlertsRequest());
    const timer = setInterval(() => dispatch(getAlertsRequest()), 60000);
    return () => clearInterval(timer);
  }, [currentBranch?.id, tatAlerts, dispatch]);

  const stats = dashboardRes?.data || {};
  const statusMap = stats.ordersByStatus || {};
  const tatData = pendingTATRes?.data || {};
  const tatOrders = tatData.orders || [];
  const alertData = alertsRes?.data || {};
  const alerts = alertData.alerts || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Orders Today */}
        <Grid item xs={12} sm={6} xl={3}>
          <StatsCard sx={{ cursor: 'pointer' }} onClick={() => navigate(`${basePath}/laboratory/orders`)}>
            <CardHeader>
              <IconWrapper color="#6366F1">
                <AssignmentOutlinedIcon />
              </IconWrapper>
            </CardHeader>
            <StatLabel>Orders Today</StatLabel>
            <StatValue>{loading ? <CircularProgress size={28} /> : stats.totalOrdersToday ?? 0}</StatValue>
            <Box sx={{ mt: 2 }}>
              <MetricRow>
                <Typography variant="caption" color="text.secondary">Completed Today</Typography>
                <Typography variant="caption" fontWeight={600} color="success.main">
                  {stats.completedToday ?? 0}
                </Typography>
              </MetricRow>
              <MetricRow
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'error.50' } }}
                onClick={(e) => { e.stopPropagation(); navigate(`${basePath}/laboratory/orders?priority=stat`); }}
              >
                <Typography variant="caption" color="text.secondary">STAT Orders</Typography>
                <Typography variant="caption" fontWeight={600} color="error.main">
                  {stats.statOrdersToday ?? 0}
                </Typography>
              </MetricRow>
            </Box>
          </StatsCard>
        </Grid>

        {/* Pending Results */}
        <Grid item xs={12} sm={6} xl={3}>
          <StatsCard sx={{ cursor: 'pointer' }} onClick={() => navigate(`${basePath}/laboratory/results`)}>
            <CardHeader>
              <IconWrapper color="#F97316">
                <EditNoteOutlinedIcon />
              </IconWrapper>
            </CardHeader>
            <StatLabel>Pending Results</StatLabel>
            <StatValue>{loading ? <CircularProgress size={28} /> : stats.pendingResults ?? 0}</StatValue>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Awaiting result entry or validation
            </Typography>
          </StatsCard>
        </Grid>

        {/* Authorization Queue */}
        <Grid item xs={12} sm={6} xl={3}>
          <StatsCard sx={{ cursor: 'pointer' }} onClick={() => navigate(`${basePath}/laboratory/results/authorization`)}>
            <CardHeader>
              <IconWrapper color="#EC4899">
                <VerifiedOutlinedIcon />
              </IconWrapper>
            </CardHeader>
            <StatLabel>Authorization Queue</StatLabel>
            <StatValue>{loading ? <CircularProgress size={28} /> : stats.awaitingAuthorization ?? 0}</StatValue>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Tech-validated, awaiting pathologist review
            </Typography>
          </StatsCard>
        </Grid>

        {/* Completed Today */}
        <Grid item xs={12} sm={6} xl={3}>
          <StatsCard sx={{ cursor: 'pointer' }} onClick={() => navigate(`${basePath}/laboratory/orders?status=verified`)}>
            <CardHeader>
              <IconWrapper color="#10B981">
                <CheckCircleOutlineIcon />
              </IconWrapper>
            </CardHeader>
            <StatLabel>Completed Today</StatLabel>
            <StatValue>{loading ? <CircularProgress size={28} /> : stats.completedToday ?? 0}</StatValue>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Verified or released
            </Typography>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Turnaround breaches needing action (lis.tatAlerts) */}
      {tatAlerts && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <SummaryCard sx={{ borderColor: alertData.openCount > 0 ? 'error.main' : 'divider', borderWidth: alertData.openCount > 0 ? 2 : 1 }}>
              <SectionHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <IconWrapper color="#DC2626" sx={{ width: 36, height: 36 }}>
                    <ReportProblemOutlinedIcon fontSize="small" />
                  </IconWrapper>
                  <Typography variant="h6" fontWeight={600}>
                    Turnaround Breaches — Action Required
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {alertData.openCount > 0 && (
                    <Chip label={`${alertData.openCount} new`} size="small" color="error" />
                  )}
                  {alertData.acknowledgedCount > 0 && (
                    <Chip label={`${alertData.acknowledgedCount} in progress`} size="small" color="warning" variant="outlined" />
                  )}
                </Box>
              </SectionHeader>
              {alerts.length > 0 ? (
                alerts.map(a => (
                  <MetricRow
                    key={a._id}
                    sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: 1 }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer' }}
                      onClick={() => navigate(`${basePath}/laboratory/orders/${a.labOrderId}`)}
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{a.orderNumber}</Typography>
                      <Typography variant="body2" color="text.secondary">{a.patient}</Typography>
                      <Chip label={a.priority} size="small" color={a.priority === 'stat' ? 'error' : a.priority === 'urgent' ? 'warning' : 'default'} sx={{ textTransform: 'uppercase' }} />
                      <Chip label={a.orderStatus} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                      <Chip label={formatOverdue(a.minutesOverdue)} size="small" color="error" />
                      {a.status === 'acknowledged' && (
                        <Chip
                          label={a.acknowledgedByName ? `Ack’d · ${a.acknowledgedByName}` : 'Acknowledged'}
                          size="small" color="warning" variant="outlined" icon={<DoneIcon />}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={a.priority === 'stat' ? 'Already STAT' : `Escalate to ${NEXT_PRIORITY[a.priority]}`}>
                        <span>
                          <Button
                            size="small" variant="outlined" color="error" startIcon={<ArrowUpwardIcon />}
                            disabled={a.priority === 'stat' || actionLoading}
                            onClick={() => dispatch(bumpOrderPriorityRequest({ orderId: a.labOrderId, priority: NEXT_PRIORITY[a.priority] }))}
                          >
                            Escalate
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        size="small" variant="contained" color="warning" startIcon={<DoneIcon />}
                        disabled={a.status === 'acknowledged' || actionLoading}
                        onClick={() => dispatch(acknowledgeAlertRequest({ id: a._id }))}
                      >
                        {a.status === 'acknowledged' ? 'Acknowledged' : 'Acknowledge'}
                      </Button>
                    </Box>
                  </MetricRow>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <Typography variant="body2">No turnaround breaches — all in-progress orders are within turnaround.</Typography>
                </Box>
              )}
            </SummaryCard>
          </Grid>
        </Grid>
      )}

      {/* In-progress orders with remaining TAT (lis.tatTracking) */}
      {tatTracking && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <SummaryCard>
              <SectionHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <IconWrapper color="#F59E0B" sx={{ width: 36, height: 36 }}>
                    <TimerOutlinedIcon fontSize="small" />
                  </IconWrapper>
                  <Typography variant="h6" fontWeight={600}>
                    Exams In Progress — Time Remaining
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {tatData.overdueCount > 0 && (
                    <Chip label={`${tatData.overdueCount} overdue`} size="small" color="error" />
                  )}
                  {tatData.dueSoonCount > 0 && (
                    <Chip label={`${tatData.dueSoonCount} due soon`} size="small" color="warning" />
                  )}
                </Box>
              </SectionHeader>
              {tatOrders.length > 0 ? (
                tatOrders.map(o => (
                  <MetricRow
                    key={o._id}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => navigate(`${basePath}/laboratory/orders/${o._id}`)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{o.labOrderId}</Typography>
                      <Typography variant="body2" color="text.secondary">{o.patient}</Typography>
                      {o.priority !== 'routine' && (
                        <Chip label={o.priority} size="small" color={o.priority === 'stat' ? 'error' : 'warning'} sx={{ textTransform: 'uppercase' }} />
                      )}
                      <Chip label={o.status} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                      {o.testCodes?.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {o.testCodes.slice(0, 5).join(', ')}{o.testCodes.length > 5 ? '…' : ''}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={formatRemaining(o.minutesRemaining)}
                      size="small"
                      color={o.isOverdue ? 'error' : o.isDueSoon ? 'warning' : 'success'}
                      variant={o.isOverdue ? 'filled' : 'outlined'}
                    />
                  </MetricRow>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <Typography variant="body2">No exams currently in progress</Typography>
                </Box>
              )}
            </SummaryCard>
          </Grid>
        </Grid>
      )}

      {/* Order Status Breakdown */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <SummaryCard>
            <SectionHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconWrapper color="#3B82F6" sx={{ width: 36, height: 36 }}>
                  <AssignmentOutlinedIcon fontSize="small" />
                </IconWrapper>
                <Typography variant="h6" fontWeight={600}>
                  Orders by Status
                </Typography>
              </Box>
            </SectionHeader>
            {Object.keys(statusMap).length > 0 ? (
              Object.entries(statusMap).map(([status, count]) => (
                <MetricRow key={status}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={status.replace(/_/g, ' ')}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{count}</Typography>
                </MetricRow>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <ScienceOutlinedIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2">No order data available</Typography>
              </Box>
            )}
          </SummaryCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SummaryCard>
            <SectionHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconWrapper color="#EF4444" sx={{ width: 36, height: 36 }}>
                  <LocalFireDepartmentOutlinedIcon fontSize="small" />
                </IconWrapper>
                <Typography variant="h6" fontWeight={600}>
                  Quick Summary
                </Typography>
              </Box>
            </SectionHeader>
            <MetricRow sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`${basePath}/laboratory/orders`)}>
              <Typography variant="body2" color="text.secondary">Total Active Orders</Typography>
              <Typography variant="body2" fontWeight={600}>
                {(statusMap.ordered ?? 0) + (statusMap.collecting ?? 0) + (statusMap.received ?? 0) + (statusMap.in_progress ?? 0)}
              </Typography>
            </MetricRow>
            <MetricRow sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`${basePath}/laboratory/results`)}>
              <Typography variant="body2" color="text.secondary">Pending Results</Typography>
              <Typography variant="body2" fontWeight={600} color="warning.main">
                {stats.pendingResults ?? 0}
              </Typography>
            </MetricRow>
            <MetricRow sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`${basePath}/laboratory/results/authorization`)}>
              <Typography variant="body2" color="text.secondary">Authorization Queue</Typography>
              <Typography variant="body2" fontWeight={600} color="info.main">
                {stats.awaitingAuthorization ?? 0}
              </Typography>
            </MetricRow>
            <MetricRow sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`${basePath}/laboratory/orders?priority=stat`)}>
              <Typography variant="body2" color="text.secondary">STAT Orders Today</Typography>
              <Typography variant="body2" fontWeight={600} color="error.main">
                {stats.statOrdersToday ?? 0}
              </Typography>
            </MetricRow>
          </SummaryCard>
        </Grid>
      </Grid>
    </Box>
  );
}
